/**
 * PageSkeleton — skeleton generico riusabile per le pagine interne.
 *
 * Replica la struttura tipica:
 *   - breadcrumb (line piccola)
 *   - titolo display (line grande)
 *   - eyebrow/momento (line piccola)
 *   - N sezioni con header + righe placeholder
 *
 * Props:
 *   - sections: numero di sezioni da renderizzare (default 3)
 *   - rowsPerSection: righe placeholder per sezione (default 4)
 *   - variant: 'list' (default) | 'grid' | 'calendar'
 *     'list'     → righe orizzontali (per liste task/MIT/azioni)
 *     'grid'     → blocchi 3-col (per dashboard/stats)
 *     'calendar' → grid 7-col (vista mese del calendario)
 *   - testid: data-testid del root (default 'page-skeleton')
 */
export default function PageSkeleton({
  sections = 3,
  rowsPerSection = 4,
  variant = 'list',
  inline = false,
  testid = 'page-skeleton',
}) {
  const Inner = (
    <>
      {!inline && (
        <>
          <div className="max-w-7xl mx-auto px-6 pt-2">
            {/* Breadcrumb */}
            <div className="h-3 w-48 bg-line animate-pulse rounded mt-3" />
          </div>
          <div className="max-w-7xl mx-auto px-6">
            {/* Header: eyebrow + titolo */}
            <div className="pt-6 pb-8 space-y-3">
              <div className="h-3 w-24 bg-line animate-pulse rounded" />
              <div className="h-10 w-2/3 max-w-xl bg-line animate-pulse rounded" />
              <div className="h-3 w-1/3 max-w-xs bg-line animate-pulse rounded" />
            </div>
          </div>
        </>
      )}

      <div className={inline ? '' : 'max-w-7xl mx-auto px-6 pb-20'}>
        {variant === 'calendar' && <CalendarSkeleton />}
        {variant === 'grid' && <GridSkeleton sections={sections} />}
        {variant === 'list' && (
          <ListSkeleton sections={sections} rowsPerSection={rowsPerSection} />
        )}
      </div>
    </>
  );

  if (inline) return <div data-testid={testid}>{Inner}</div>;

  return (
    <div className="min-h-screen bg-canvas text-ink" data-testid={testid}>
      {Inner}
    </div>
  );
}

/** Layout 'list': sezioni verticali con righe simili a item lists (task/KR/MIT). */
function ListSkeleton({ sections, rowsPerSection }) {
  return (
    <div className="space-y-10">
      {Array.from({ length: sections }).map((_, s) => (
        <section key={s}>
          {/* Sezione header: titolo + barra progresso */}
          <div className="flex items-baseline gap-3 mb-3 pb-1 border-b border-line">
            <div className="h-3 w-3 bg-line animate-pulse rounded" />
            <div className="h-3 w-32 bg-line animate-pulse rounded" />
            <div className="h-3 w-12 bg-line animate-pulse rounded" />
            <div className="flex-1 h-1 bg-linesoft animate-pulse rounded ml-1" />
          </div>
          {/* Righe placeholder */}
          <ul className="divide-y divide-linesoft">
            {Array.from({ length: rowsPerSection }).map((_, r) => (
              <li key={r} className="py-3 flex items-center gap-3">
                <div className="h-4 w-4 bg-line animate-pulse rounded shrink-0" />
                <div className="h-3 w-10 bg-line animate-pulse rounded shrink-0" />
                <div className="h-4 flex-1 max-w-md bg-line animate-pulse rounded" />
                <div className="h-3 w-20 bg-line animate-pulse rounded shrink-0" />
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

/** Layout 'grid': 3 colonne per pagine tipo stats/dashboard riassuntivo. */
function GridSkeleton({ sections }) {
  return (
    <div className="space-y-10">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={`sk-grid-card-${i}`} className="border border-line p-5 space-y-3 bg-paper">
            <div className="h-3 w-24 bg-line animate-pulse rounded" />
            <div className="h-10 w-3/4 bg-line animate-pulse rounded" />
            <div className="h-2 w-full bg-line animate-pulse rounded" />
          </div>
        ))}
      </div>
      {Array.from({ length: Math.max(0, sections - 1) }).map((_, s) => (
        <section key={s} className="space-y-3">
          <div className="h-3 w-32 bg-line animate-pulse rounded" />
          <div className="h-40 bg-canvas border border-linesoft animate-pulse" />
        </section>
      ))}
    </div>
  );
}

/** Layout 'calendar': griglia 7 colonne × 5 righe per la vista mese. */
function CalendarSkeleton() {
  return (
    <div className="space-y-3">
      {/* Header giorni della settimana */}
      <div className="grid grid-cols-7 gap-px">
        {Array.from({ length: 7 }).map((_, i) => (
          <div key={`sk-cal-head-${i}`} className="h-3 bg-line animate-pulse rounded" />
        ))}
      </div>
      {/* 5 righe × 7 colonne di celle */}
      <div className="grid grid-cols-7 gap-px bg-line border border-line">
        {Array.from({ length: 35 }).map((_, i) => (
          <div key={`sk-cal-cell-${i}`} className="bg-paper aspect-square p-2 space-y-2">
            <div className="h-3 w-6 bg-line animate-pulse rounded" />
            <div className="h-2 w-full bg-linesoft animate-pulse rounded" />
            <div className="h-2 w-3/4 bg-linesoft animate-pulse rounded" />
          </div>
        ))}
      </div>
    </div>
  );
}
